import { useState, useRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { DiscoverSessionProvider } from "@/contexts/DiscoverSessionContext";
import BottomNav from "@/components/BottomNav";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppInitializer from "@/components/AppInitializer";
import Index from "./pages/Index";
import Demo from "./pages/Demo";
import DemoEnd from "./pages/DemoEnd";
import Home from "./pages/Home";
import WatchHistory from "./pages/WatchHistory";
import ReviewPending from "./pages/ReviewPending";
import Onboarding from "./pages/Onboarding";
import Bookmarks from "./pages/Bookmarks";
import HiddenTitles from "./pages/HiddenTitles";
import Discover from "./pages/Discover";
// Shortlist page removed — "tonight" picks are session-only
import Settings from "./pages/Settings";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Landing from "./pages/Landing";
import PaymentSuccess from "./pages/PaymentSuccess";
import Friends from "./pages/Friends";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Guides from "./pages/Guides";
import Blog from "./pages/Blog";
import BlogEditorPage from "./pages/BlogEditor";
import FriendNotificationListener from "@/components/FriendNotificationListener";
import CookieConsent from "@/components/CookieConsent";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useAppUpdate } from "@/hooks/useAppUpdate";

/** Allow guest access to /discover when a join code is present (or was present) in the URL */
const DiscoverRoute = () => {
  const [searchParams] = useSearchParams();
  const guestAccessGranted = useRef(false);
  if (searchParams.get("join")) guestAccessGranted.current = true;
  if (guestAccessGranted.current) return <Discover />;
  return <ProtectedRoute><Discover /></ProtectedRoute>;
};

const queryClient = new QueryClient();

const App = () => {
  useAppUpdate();
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <CookieConsent />
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <DiscoverSessionProvider>
            <AppInitializer />
            <FriendNotificationListener />
            <main className="max-w-lg mx-auto relative">
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/landing" element={<Landing />} />
                <Route path="/demo" element={<Demo />} />
                <Route path="/demo-end" element={<DemoEnd />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
                <Route path="/discover" element={<DiscoverRoute />} />
                <Route path="/watch-history" element={<ProtectedRoute><WatchHistory /></ProtectedRoute>} />
                <Route path="/bookmarks" element={<ProtectedRoute><Bookmarks /></ProtectedRoute>} />
                <Route path="/hidden-titles" element={<ProtectedRoute><HiddenTitles /></ProtectedRoute>} />
                <Route path="/review" element={<ProtectedRoute><ReviewPending /></ProtectedRoute>} />
                {/* Shortlist route removed */}
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/friends" element={<ProtectedRoute><Friends /></ProtectedRoute>} />
                <Route path="/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/about" element={<About />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/guides" element={<Guides />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/new" element={<ProtectedRoute><BlogEditorPage /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
              <BottomNav />
            </main>
          </DiscoverSessionProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};


export default App;
